import type { Finding } from './api.ts'

/**
 * The rótulo's findings, in the words the Rótulo section shows (#438).
 *
 * Every label finding carries `evidence.field` -- which part of the label it
 * is about -- and `prescribed`, `keyed` and `printed`: what the receita says,
 * what the sistema has, and what the label says. The label is judged against
 * the receita, and against the sistema only where the receita is silent;
 * the row shows the receita beside the label, and the icon says when the
 * sistema stood in. Label findings are kept away from the explainer, so every
 * sentence a label row needs is written here, the same on every pote.
 *
 * Findings scored before #463 carried `expected` and `source` instead, and
 * are read the same way.
 */

/** Mirrors `LabelField` in `@rp/core`; the screen reads it back as text. */
export const LABEL_FIELD_LABEL: Readonly<Record<string, string>> = {
  absent: 'Rótulo',
  patient: 'Paciente',
  prescriber: 'Prescritor',
  posology: 'Posologia',
  composition: 'Composição',
  quantity: 'Quantidade',
  form: 'Forma',
}

/**
 * The order the fields are shown, under the printed label: who it is for and
 * who prescribed it first, as on every other step, then what is in the pot.
 */
const FIELD_ORDER = [
  'absent',
  'patient',
  'prescriber',
  'composition',
  'form',
  'quantity',
  'posology',
]

function evidenceOf(finding: Finding): Record<string, unknown> {
  return (finding.evidence ?? {}) as Record<string, unknown>
}

export function labelField(finding: Finding): string {
  const field = evidenceOf(finding).field
  return typeof field === 'string' ? field : 'absent'
}

export function sortLabelFindings(findings: readonly Finding[]): Finding[] {
  const rank = (f: Finding) => {
    const at = FIELD_ORDER.indexOf(labelField(f))
    return at === -1 ? FIELD_ORDER.length : at
  }
  return [...findings].sort((a, b) => rank(a) - rank(b))
}

/** The two column headings of a label row: the receita, then the label. */
export const LABEL_HEADINGS: readonly [string, string] = ['Receita', 'No rótulo']

function asText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

/** What the receita says, reading the older shape too. */
function prescribedOf(evidence: Record<string, unknown>): string | undefined {
  if ('prescribed' in evidence) return asText(evidence.prescribed)
  return evidence.source === 'receita' ? asText(evidence.expected) : undefined
}

/** What the sistema has, reading the older shape too. */
function keyedOf(evidence: Record<string, unknown>): string | undefined {
  if ('keyed' in evidence) return asText(evidence.keyed)
  return evidence.source === 'sistema' ? asText(evidence.expected) : undefined
}

export function labelSides(finding: Finding): {
  prescribed: string | undefined
  quoted: string | undefined
} {
  const evidence = evidenceOf(finding)
  return { prescribed: prescribedOf(evidence), quoted: asText(evidence.printed) }
}

/**
 * When the receita said nothing and the sistema stood in, what the sistema had.
 *
 * Said even on a row that agreed: the receita column is empty there, and
 * without this nobody could tell what the label was held against.
 */
export function labelStandIn(finding: Finding): string | undefined {
  const evidence = evidenceOf(finding)
  const prescribed = prescribedOf(evidence)
  const keyed = keyedOf(evidence)
  if (keyed === undefined) return undefined
  if (prescribed === undefined) {
    return `A receita não informa; o rótulo foi comparado com o sistema: ${keyed}.`
  }
  // The receita's amount is in another unit than the label's (g against ml).
  if (evidence.receitaIncomparable === true) {
    return `A receita (${prescribed}) não se compara com a unidade do rótulo; o rótulo foi comparado com o sistema: ${keyed}.`
  }
  return undefined
}

/** Why a label row reads the way it does, when the two values do not say. */
export function labelReason(finding: Finding): string | undefined {
  const why = labelWhy(finding)
  const standIn = labelStandIn(finding)
  if (why === undefined) return standIn
  return standIn === undefined ? why : `${why} ${standIn}`
}

function labelWhy(finding: Finding): string | undefined {
  const evidence = evidenceOf(finding)
  const field = labelField(finding)
  const reference =
    prescribedOf(evidence) !== undefined && evidence.receitaIncomparable !== true
      ? 'a receita'
      : 'o sistema'

  if (field === 'absent') {
    return 'O rótulo deste pote não foi encontrado no sistema. Conferir o rótulo impresso.'
  }
  if (evidence.truncated === true) {
    return 'O nome aparece cortado no rótulo, no limite do campo.'
  }
  if (evidence.answeredIn === 'posology') {
    return 'A posologia do rótulo é a que foi comparada com a receita: responder em Posologia.'
  }
  if (field === 'composition') {
    const missing = Array.isArray(evidence.missing) ? (evidence.missing as string[]) : []
    const differs = Array.isArray(evidence.doseDiffers)
      ? (evidence.doseDiffers as Array<{
          name: string
          expected?: string
          keyed?: string
          printed: string
        }>)
      : []
    const onReceita = reference === 'a receita'
    const where = onReceita ? 'na receita' : 'no sistema'
    const from = onReceita ? 'da receita' : 'do sistema'
    const parts = [
      ...(missing.length > 0 ? [`não está no rótulo: ${missing.join(', ')}`] : []),
      ...differs.map((d) => `${d.name}: ${d.expected ?? d.keyed} ${where}, ${d.printed} no rótulo`),
    ]
    if (parts.length > 0) return `Composição do rótulo difere ${from} — ${parts.join('; ')}.`
  }

  switch (evidence.reason) {
    case 'the label names none':
      return field === 'patient'
        ? 'O rótulo não traz o nome do paciente.'
        : 'O rótulo não traz o nome do prescritor.'
    case 'neither side names one':
      return 'Nem a receita nem o sistema trazem este nome para comparar.'
    case 'the label lists no ingredient':
      return 'Não foi possível localizar os ativos no rótulo. Conferir o rótulo.'
    case 'the label states no amount':
      return 'O rótulo não informa a quantidade (Contém/Vol).'
    case 'nothing keyed to compare':
    case 'nothing to compare':
      return 'Nem a receita nem o sistema informam a quantidade para comparar com o rótulo.'
    case 'the keyed instruction was not found on the label':
      return 'A posologia digitada não foi encontrada no rótulo: o texto pode ter sido reescrito. Conferir o rótulo.'
  }
  if (finding.disposition === 'divergence') {
    return `${LABEL_FIELD_LABEL[field] ?? 'Rótulo'}: o rótulo não diz o mesmo que ${reference}.`
  }
  if (finding.disposition === 'undecided') return 'Não foi possível conferir este campo do rótulo.'
  return undefined
}
